import React, { useState } from 'react';
import { 
  Building, ShieldCheck, Cpu, HardDrive, Database, Settings, Plus, UserPlus, Check, X, Shield, Lock, Zap, CheckCircle2, AlertTriangle, Grid, Server, Activity, DollarSign
} from 'lucide-react';
import { ModuleExplorer } from './ModuleExplorer';

export interface TenantAdmin {
  id: string;
  adminName: string;
  adminEmail: string;
  schoolName: string;
  slug: string;
  plan: string;
  modules: string[];
  studentCount: number;
  subscriptionStatus: 'ACTIVE' | 'EXPIRED' | 'PENDING_ACTIVATION';
  subscriptionExpiry: string;
}

const ALL_30_MODULES = [
  { key: 'aac', label: '1. AAC Speech Synthesizer' },
  { key: 'speech-therapy', label: '2. Speech & Articulation' },
  { key: 'autism', label: '3. Autism & ABA Behavior' },
  { key: 'sign-language', label: '4. Sign Language & Gestures' },
  { key: 'braille', label: '5. Braille & Tactile Learning' },
  { key: 'student', label: '6. Student & IEP Progress' },
  { key: 'therapist', label: '7. Therapist Workspace & SOAP' },
  { key: 'teacher', label: '8. Teacher & Classroom' },
  { key: 'assessment', label: '9. Diagnostic Assessment' },
  { key: 'school-erp', label: '10. Special School ERP' },
  { key: 'hospital', label: '11. Rehabilitation Hospital' },
  { key: 'billing', label: '12. NDIS & Insurance Billing' },
  { key: 'communication', label: '13. Parent Communication' },
  { key: 'inventory', label: '14. Assistive Device Inventory' },
  { key: 'analytics', label: '15. Executive Analytics BI' },
  { key: 'ai', label: '16. AI Copilot Summarizer' },
  { key: 'sensory-room', label: '17. Sensory Room Controller' },
  { key: 'tele-rehab', label: '18. Tele-Rehabilitation Video' },
  { key: 'early-intervention', label: '19. Early Intervention Milestone' },
  { key: 'vocational-living', label: '20. Vocational & Life Skills' },
  { key: 'hydrotherapy', label: '21. Hydrotherapy Aquatic Rehab' },
  { key: 'music-therapy', label: '22. Music Auditory Stimulation' },
  { key: 'animal-therapy', label: '23. Animal-Assisted Therapy' },
  { key: 'prosthetics', label: '24. Bionic Prosthetics & Exoskeleton' },
  { key: 'vbmapp-assessment', label: '25. VB-MAPP Assessment Protocol' },
  { key: 'core-vocab-ai', label: '26. Motor Core Vocab & AI' },
  { key: 'fim-weefim', label: '27. FIM Functional Scoring' },
  { key: 'evv-gps-tracker', label: '28. EVV Visit Verification & GPS' },
  { key: 'gamified-neuro', label: '29. WebGL Biofeedback Neuro-Rehab' },
  { key: 'transition-matrix', label: '30. Transition & Vocational Matrix' }
];

const ALL_MODULE_KEYS = ALL_30_MODULES.map(m => m.key);

const INITIAL_TENANT_ADMINS: TenantAdmin[] = [
  {
    id: 'demo-user',
    adminName: 'Demo Admin User',
    adminEmail: 'demo@bugfreesolution.com',
    schoolName: 'BugFree Solutions Demo Organization',
    slug: 'bugfree-demo',
    plan: 'ENTERPRISE_FULL_SUITE',
    studentCount: 175,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2028-12-31',
    modules: ALL_MODULE_KEYS
  },
  { 
    id: '1', 
    adminName: 'Dr. Robert Vance', 
    adminEmail: 'principal@stjude.edu', 
    schoolName: 'St. Jude Special School', 
    slug: 'stjude', 
    plan: 'ENTERPRISE_SCHOOL', 
    studentCount: 142,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2027-08-31',
    modules: ALL_MODULE_KEYS
  },
  { 
    id: '2', 
    adminName: 'Dr. Ananya Sharma', 
    adminEmail: 'admin@sunshinetherapy.org', 
    schoolName: 'Sunshine Therapy Centre', 
    slug: 'sunshine', 
    plan: 'PRO_CLINIC', 
    studentCount: 88,
    subscriptionStatus: 'ACTIVE',
    subscriptionExpiry: '2027-04-15',
    modules: ['aac', 'speech-therapy', 'therapist', 'billing', 'communication', 'early-intervention', 'tele-rehab'] 
  },
  { 
    id: '3', 
    adminName: 'Superintendent K. Patil', 
    adminEmail: 'director@districtrehab.gov', 
    schoolName: 'District Rehab Department', 
    slug: 'rehab', 
    plan: 'GOVT_REHAB', 
    studentCount: 350,
    subscriptionStatus: 'EXPIRED',
    subscriptionExpiry: '2026-06-01',
    modules: ALL_MODULE_KEYS 
  }
];

export const SuperAdminDashboard: React.FC = () => {
  const [tenantAdmins, setTenantAdmins] = useState<TenantAdmin[]>(INITIAL_TENANT_ADMINS);
  const [activeTab, setActiveTab] = useState<'licensing' | 'modules_explorer' | 'server_health'>('licensing');
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);

  // New Admin Form State
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [plan, setPlan] = useState('ENTERPRISE_FULL_SUITE');
  const [selectedModules, setSelectedModules] = useState<string[]>(ALL_MODULE_KEYS);
  const [successMsg, setSuccessMsg] = useState('');

  const toggleModuleForAdmin = (adminId: string, moduleKey: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const exists = item.modules.includes(moduleKey);
        const updated = exists
          ? item.modules.filter((m) => m !== moduleKey)
          : [...item.modules, moduleKey];
        return { ...item, modules: updated };
      })
    );
  };

  const grantAllModulesToAdmin = (adminId: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        return { ...item, modules: ALL_MODULE_KEYS, subscriptionStatus: 'ACTIVE' };
      })
    );
    setSuccessMsg('Granted ALL 30 Modules & activated subscription!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const toggleSubscriptionStatus = (adminId: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const newStatus = item.subscriptionStatus === 'ACTIVE' ? 'EXPIRED' : 'ACTIVE';
        return { 
          ...item, 
          subscriptionStatus: newStatus,
          subscriptionExpiry: newStatus === 'ACTIVE' ? '2028-12-31' : 'Expired (Action Required)'
        };
      })
    );
    setSuccessMsg('Updated tenant subscription status successfully!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleCreateTenantAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminName || !adminEmail || !schoolName) return;

    const slug = schoolName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const newAdmin: TenantAdmin = {
      id: Date.now().toString(),
      adminName,
      adminEmail,
      schoolName,
      slug,
      plan,
      studentCount: 0,
      subscriptionStatus: 'ACTIVE',
      subscriptionExpiry: '2028-12-31',
      modules: selectedModules
    };

    setTenantAdmins([newAdmin, ...tenantAdmins]);
    setSuccessMsg(`Tenant Admin "${adminName}" created & active subscription granted for ${schoolName}!`);
    setAdminName('');
    setAdminEmail('');
    setSchoolName('');
    setIsAddAdminOpen(false);

    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Super Admin Control Plane Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 text-white p-6 rounded-3xl shadow-xl border border-blue-500/30 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full uppercase tracking-wider mb-2 border border-emerald-400/30">
            <Shield className="w-3.5 h-3.5" /> Super Admin Plane • WHM Root Controller
          </div>
          <h2 className="text-2xl md:text-3xl font-black">Global Multi-Tenant Licensing & Server Control</h2>
          <p className="text-xs text-blue-200 mt-1">Manage 1,400+ Special Schools, grant valid subscriptions, and authorize all 30 modules across tenants.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsAddAdminOpen(true)}
            className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow-lg transition"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Tenant Admin</span>
          </button>
        </div>
      </div>

      {/* Super Admin Navigation Tabs */}
      <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setActiveTab('licensing')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              activeTab === 'licensing' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Tenant Licensing & Subscription Matrix</span>
          </button>

          <button
            onClick={() => setActiveTab('modules_explorer')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              activeTab === 'modules_explorer' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Grid className="w-4 h-4 text-amber-400" />
            <span>🚀 30-Module Suite Launcher</span>
          </button>

          <button
            onClick={() => setActiveTab('server_health')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              activeTab === 'server_health' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Server className="w-4 h-4" />
            <span>cPanel MultiPHP 8.4 Server Health</span>
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm">
          <Check className="w-4 h-4 text-emerald-600" />
          {successMsg}
        </div>
      )}

      {/* Global System Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><Building className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">{tenantAdmins.length}</div>
            <div className="text-xs text-slate-500 font-semibold">Active Tenant Admins</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl"><Zap className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">
              {tenantAdmins.filter(t => t.subscriptionStatus === 'ACTIVE').length}
            </div>
            <div className="text-xs text-slate-500 font-semibold">Active Subscriptions</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-xl"><Grid className="w-6 h-6 text-purple-600" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">30 / 30</div>
            <div className="text-xs text-slate-500 font-semibold">Authorized Modules</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl"><DollarSign className="w-6 h-6" /></div>
          <div>
            <div className="text-xl font-black text-slate-900">$48,500</div>
            <div className="text-xs text-slate-500 font-semibold">Monthly Recurring Revenue</div>
          </div>
        </div>
      </div>

      {/* TAB 1: TENANT LICENSING MATRIX */}
      {activeTab === 'licensing' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-6">
          <div className="flex items-center justify-between border-b pb-4">
            <div>
              <h3 className="text-lg font-extrabold text-slate-900">Tenant Administrator Accounts & Subscription Licensing</h3>
              <p className="text-xs text-slate-500">Grant active subscriptions and toggle individual module authorizations per tenant</p>
            </div>
          </div>

          <div className="space-y-6">
            {tenantAdmins.map((tenant) => (
              <div key={tenant.id} className="p-5 border border-slate-200 rounded-2xl bg-slate-50/50 space-y-4">
                
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-200 pb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-extrabold text-slate-900 text-base">{tenant.schoolName}</h4>
                      <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                        subdomain: {tenant.slug}.abilityos.com
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Admin: <span className="font-bold text-slate-700">{tenant.adminName}</span> ({tenant.adminEmail}) • Plan: <span className="font-bold text-blue-600">{tenant.plan}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => grantAllModulesToAdmin(tenant.id)}
                      className="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold shadow transition"
                    >
                      Grant All 30 Modules
                    </button>

                    <button
                      onClick={() => toggleSubscriptionStatus(tenant.id)}
                      className={`px-3 py-1 rounded-lg text-xs font-bold border shadow-sm transition ${
                        tenant.subscriptionStatus === 'ACTIVE'
                          ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                          : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300'
                      }`}
                    >
                      {tenant.subscriptionStatus === 'ACTIVE' ? 'Revoke Subscription' : 'Grant Valid Subscription'}
                    </button>
                  </div>
                </div>

                {/* 30 Module Authorizations for this Tenant */}
                <div>
                  <div className="flex items-center justify-between text-[11px] font-extrabold text-slate-500 uppercase tracking-wider mb-2">
                    <span>Enabled Modules ({tenant.modules.length} of 30 Authorized):</span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                    {ALL_30_MODULES.map((m) => {
                      const isEnabled = tenant.modules.includes(m.key);
                      return (
                        <button
                          key={m.key}
                          onClick={() => toggleModuleForAdmin(tenant.id, m.key)}
                          className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition flex items-center justify-between ${
                            isEnabled 
                              ? 'bg-blue-600 text-white shadow-sm' 
                              : 'bg-white text-slate-400 border border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <span className="truncate pr-1">{m.label}</span>
                          {isEnabled ? <Check className="w-3.5 h-3.5 text-amber-300 shrink-0" /> : <X className="w-3 h-3 text-slate-300 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: DIRECT 30-MODULE SUITE LAUNCHER */}
      {activeTab === 'modules_explorer' && (
        <div className="space-y-4">
          <ModuleExplorer />
        </div>
      )}

      {/* TAB 3: SERVER HEALTH */}
      {activeTab === 'server_health' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <h3 className="text-lg font-extrabold text-slate-900 border-b pb-3">cPanel MultiPHP 8.4 & Database Telemetry</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <div className="text-xs font-bold text-slate-500">PHP Version</div>
              <div className="text-lg font-black text-emerald-600 font-mono">PHP 8.4.3 (ea-php84)</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <div className="text-xs font-bold text-slate-500">MariaDB Storage Engine</div>
              <div className="text-lg font-black text-blue-600 font-mono">10.11.8-MariaDB (InnoDB)</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <div className="text-xs font-bold text-slate-500">Server Host / SSL</div>
              <div className="text-lg font-black text-purple-600 font-mono">HTTPS TLS 1.3 Active</div>
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Admin Modal */}
      {isAddAdminOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-blue-900 text-white p-6 flex justify-between items-center">
              <h3 className="text-lg font-bold">Add New Tenant Administrator</h3>
              <button onClick={() => setIsAddAdminOpen(false)} className="text-blue-200 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleCreateTenantAdmin} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">School / Clinic Name</label>
                <input
                  type="text" required
                  value={schoolName} onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="e.g. St. Xavier Rehabilitation School"
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Admin Full Name</label>
                  <input
                    type="text" required
                    value={adminName} onChange={(e) => setAdminName(e.target.value)}
                    placeholder="Dr. Sarah Connor"
                    className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Admin Email</label>
                  <input
                    type="email" required
                    value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="sarah@stxavier.edu"
                    className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Subscription Plan</label>
                <select
                  value={plan} onChange={(e) => setPlan(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ENTERPRISE_FULL_SUITE">Enterprise Full Suite ($499/mo)</option>
                  <option value="PRO_CLINIC">Pro Rehabilitation Clinic ($299/mo)</option>
                  <option value="GOVT_REHAB">Government Rehab Department ($999/mo)</option>
                </select>
              </div>

              <div className="pt-3 border-t flex justify-end gap-3">
                <button type="button" onClick={() => setIsAddAdminOpen(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-xs">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow">Provision Admin & Activate Subscription</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
